// Prompt 0454 — Toolbar Tiptap rica compartida por cada seccion de la ficha.
// Prompt 0456 — Movida a ficha-shared/ para consumo desde puesto-panel + rol-funcional-panel
// sin acoplamiento cruzado entre paneles (regla "modulos estancos" de CLAUDE.md).
// SVG inline minimalistas (regla frontend.md: nunca emojis, nunca librerias de iconos).

import type React from 'react';
import { type Editor } from '@tiptap/react';
import { useI18n } from '../../../../../../i18n';
import {
  IconBold,
  IconItalic,
  IconUnderline,
  IconBullet,
  IconOrdered,
  IconQuote,
  IconAlign,
  IconClear,
  IconUndo,
  IconRedo,
} from './icons';

interface FichaToolbarProps {
  editor: Editor | null;
}

export function FichaToolbar({ editor }: FichaToolbarProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const panel = (lbl.puestoPanel ?? {}) as unknown as Record<string, string>;
  const T = (key: string, fallback: string) => (panel[key] as string | undefined) ?? fallback;

  if (!editor) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 3,
        padding: 6,
        border: '1px solid #E5E7EB',
        borderBottom: 'none',
        borderTopLeftRadius: 6,
        borderTopRightRadius: 6,
        background: '#F8FAFC',
      }}
    >
      {/* Headings */}
      <ToolbarBtn
        editor={editor}
        active={editor.isActive('heading', { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        title={T('toolbarHeading1', 'Titulo 1')}
      >
        H1
      </ToolbarBtn>
      <ToolbarBtn
        editor={editor}
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        title={T('toolbarHeading2', 'Titulo 2')}
      >
        H2
      </ToolbarBtn>
      <ToolbarBtn
        editor={editor}
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        title={T('toolbarHeading3', 'Titulo 3')}
      >
        H3
      </ToolbarBtn>

      <Separator />

      {/* Texto */}
      <ToolbarBtn
        editor={editor}
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title={T('toolbarBold', 'Negrita')}
      >
        <IconBold />
      </ToolbarBtn>
      <ToolbarBtn
        editor={editor}
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title={T('toolbarItalic', 'Cursiva')}
      >
        <IconItalic />
      </ToolbarBtn>
      <ToolbarBtn
        editor={editor}
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        title={T('toolbarUnderline', 'Subrayado')}
      >
        <IconUnderline />
      </ToolbarBtn>

      <Separator />

      {/* Listas */}
      <ToolbarBtn
        editor={editor}
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title={T('toolbarBulletList', 'Lista')}
      >
        <IconBullet />
      </ToolbarBtn>
      <ToolbarBtn
        editor={editor}
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title={T('toolbarOrderedList', 'Lista numerada')}
      >
        <IconOrdered />
      </ToolbarBtn>
      <ToolbarBtn
        editor={editor}
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        title={T('toolbarBlockquote', 'Cita')}
      >
        <IconQuote />
      </ToolbarBtn>

      <Separator />

      {/* Alineacion */}
      <ToolbarBtn
        editor={editor}
        active={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        title={T('toolbarAlignLeft', 'Alinear izquierda')}
      >
        <IconAlign dir="left" />
      </ToolbarBtn>
      <ToolbarBtn
        editor={editor}
        active={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        title={T('toolbarAlignCenter', 'Alinear centro')}
      >
        <IconAlign dir="center" />
      </ToolbarBtn>
      <ToolbarBtn
        editor={editor}
        active={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        title={T('toolbarAlignRight', 'Alinear derecha')}
      >
        <IconAlign dir="right" />
      </ToolbarBtn>

      <Separator />

      {/* Color */}
      <ColorSwatch editor={editor} color="#000000" title={T('toolbarColorBlack', 'Negro')} />
      <ColorSwatch editor={editor} color="#1F3864" title={T('toolbarColorBlue', 'Azul')} />
      <ColorSwatch editor={editor} color="#DC2626" title={T('toolbarColorRed', 'Rojo')} />
      <ColorSwatch editor={editor} color="#16A34A" title={T('toolbarColorGreen', 'Verde')} />

      <Separator />

      {/* Limpiar */}
      <ToolbarBtn
        editor={editor}
        active={false}
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        title={T('toolbarClearFormat', 'Limpiar formato')}
      >
        <IconClear />
      </ToolbarBtn>

      <Separator />

      {/* Historia */}
      <ToolbarBtn
        editor={editor}
        active={false}
        onClick={() => editor.chain().focus().undo().run()}
        title={T('toolbarUndo', 'Deshacer')}
      >
        <IconUndo />
      </ToolbarBtn>
      <ToolbarBtn
        editor={editor}
        active={false}
        onClick={() => editor.chain().focus().redo().run()}
        title={T('toolbarRedo', 'Rehacer')}
      >
        <IconRedo />
      </ToolbarBtn>
    </div>
  );
}

function ToolbarBtn({
  active,
  onClick,
  title,
  children,
}: {
  editor: Editor;
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        padding: '4px 8px',
        border: '1px solid #E5E7EB',
        background: active ? '#1F3864' : '#fff',
        color: active ? '#fff' : '#1F3864',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
        minWidth: 26,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
      }}
    >
      {children}
    </button>
  );
}

function ColorSwatch({ editor, color, title }: { editor: Editor; color: string; title: string }) {
  return (
    <button
      type="button"
      onClick={() => editor.chain().focus().setColor(color).run()}
      title={title}
      style={{
        width: 22,
        height: 22,
        border: '1px solid #E5E7EB',
        background: color,
        borderRadius: 4,
        cursor: 'pointer',
        padding: 0,
      }}
      aria-label={title}
    />
  );
}

function Separator() {
  return <div style={{ width: 1, height: 18, background: '#E5E7EB', margin: '0 2px' }} />;
}

// Iconos SVG movidos a ./icons/index.tsx en el prompt 0457 (T-3) —
// se consumen via named imports al inicio del archivo.
